import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './Credits.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const BuyCredits = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { token } = useAuth();
  const [packages, setPackages] = useState([]);
  const [customLimits, setCustomLimits] = useState({ min: 10, max: 100000, step: 1 });
  const [selected, setSelected] = useState(null);
  const [customCredits, setCustomCredits] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [purchase, setPurchase] = useState(null);
  const [bankDetails, setBankDetails] = useState(null);
  const [proofImage, setProofImage] = useState(null);
  const [proofPreview, setProofPreview] = useState(null);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const [resumeLoading, setResumeLoading] = useState(false);

  const resumeId = searchParams.get('resume');

  useEffect(() => {
    if (resumeId) return;

    const load = async () => {
      try {
        const authToken = token || localStorage.getItem('marilink_token');
        const res = await fetch(`${API_BASE}/credits/packages`, {
          headers: { Authorization: `Bearer ${authToken}` }
        });
        const data = await res.json();
        if (data.success) {
          setPackages(data.packages || []);
          if (data.customCredits) setCustomLimits(data.customCredits);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token, resumeId]);

  useEffect(() => {
    if (!resumeId) return;

    let cancelled = false;

    const loadResume = async () => {
      setResumeLoading(true);
      setError('');

      try {
        const authToken = token || localStorage.getItem('marilink_token');
        const res = await fetch(`${API_BASE}/credits/purchase/${encodeURIComponent(resumeId)}`, {
          headers: { Authorization: `Bearer ${authToken}` }
        });
        const data = await res.json();

        if (cancelled) return;

        if (!data.success || !data.purchase) {
          throw new Error(data.message || 'Unable to load this purchase.');
        }

        if (data.purchase.status !== 'pending_payment') {
          throw new Error('This purchase is no longer awaiting payment.');
        }

        setPurchase(data.purchase);
        setBankDetails(data.purchase.bankDetailsUsed || null);
      } catch (e) {
        if (!cancelled) {
          setError(e.message || 'Unable to resume purchase.');
          setSearchParams({}, { replace: true });
        }
      } finally {
        if (!cancelled) {
          setResumeLoading(false);
          setLoading(false);
        }
      }
    };

    loadResume();

    return () => {
      cancelled = true;
    };
  }, [resumeId, token, setSearchParams]);

  const customNumber = parseInt(customCredits, 10);
  const isCustomValid =
    Number.isInteger(customNumber) &&
    customNumber >= customLimits.min &&
    customNumber <= customLimits.max &&
    customNumber % customLimits.step === 0;

  const effectiveCredits = selected ? selected.credits : (isCustomValid ? customNumber : 0);
  const effectiveAmount = selected ? selected.price : (isCustomValid ? customNumber : 0);
  const canContinue = effectiveCredits > 0;

  const handleSelectPackage = (pkg) => {
    setSelected(pkg);
    setCustomCredits('');
  };

  const handleCustomChange = (e) => {
    const val = e.target.value;
    if (val === '' || /^\d+$/.test(val)) {
      setCustomCredits(val);
      setSelected(null);
    }
  };

  const handleQuickAdd = (amount) => {
    const next = (customNumber || 0) + amount;
    setCustomCredits(String(Math.min(next, customLimits.max)));
    setSelected(null);
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      setError('File too large. Max 15MB.');
      return;
    }
    setProofImage(file);
    const reader = new FileReader();
    reader.onloadend = () => setProofPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const convertToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const r = new FileReader();
      r.readAsDataURL(file);
      r.onload = () => resolve(r.result);
      r.onerror = reject;
    });

  const handleCreatePurchase = async () => {
    if (!canContinue) {
      setError('Please select a package or enter a valid custom amount');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const authToken = token || localStorage.getItem('marilink_token');
      const body = selected
        ? { packageId: selected.id, currency: 'USD' }
        : { customCredits: customNumber, currency: 'USD' };

      const res = await fetch(`${API_BASE}/credits/purchase`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (data.success) {
        setPurchase(data.purchase);
        setBankDetails(data.bankDetails || data.purchase?.bankDetailsUsed || null);
      } else {
        setError(data.message || 'Failed to create purchase');
      }
    } catch (e) {
      setError('Network error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitProof = async () => {
    if (!proofImage) {
      setError('Please upload payment proof');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const authToken = token || localStorage.getItem('marilink_token');
      const base64 = await convertToBase64(proofImage);
      const res = await fetch(`${API_BASE}/credits/purchase/${purchase._id}/proof`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ imageUrl: base64, notes })
      });
      const data = await res.json();
      if (data.success) {
        navigate('/credits');
      } else {
        setError(data.message || 'Failed to upload proof');
      }
    } catch (e) {
      setError('Network error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStartNewPurchase = () => {
    setPurchase(null);
    setBankDetails(null);
    setProofImage(null);
    setProofPreview(null);
    setNotes('');
    setError('');
    setSearchParams({}, { replace: true });
  };

  const handleBackToCredits = () => {
    navigate('/credits');
  };

  if (loading || resumeLoading) {
    return (
      <div className="cr-page">
        <div className="cr-loading">
          <i className="fas fa-spinner fa-spin"></i>
        </div>
      </div>
    );
  }

  return (
    <div className="cr-page">
      <div className="cr-header">
        <div>
          <h1>
            <i className="fas fa-shopping-cart"></i> Buy Credits
          </h1>
          <p>Purchase credits to use for contract payments (1 USD = 1 Credit)</p>
        </div>
        <button className="cr-btn cr-btn-secondary" onClick={handleBackToCredits}>
          <i className="fas fa-arrow-left"></i> Back
        </button>
      </div>

      {error && (
        <div className="cr-error-banner">
          <i className="fas fa-exclamation-circle"></i> {error}
        </div>
      )}

      {purchase && resumeId && (
        <div className="cr-section" style={{ padding: '12px 16px', marginBottom: 20 }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 12
            }}
          >
            <span style={{ fontSize: 13, color: '#888' }}>
              Resuming purchase <strong style={{ color: '#18cb96' }}>{purchase.purchaseId}</strong>
            </span>
            <button
              type="button"
              className="cr-btn cr-btn-secondary"
              onClick={handleStartNewPurchase}
              style={{ padding: '6px 12px', fontSize: 12 }}
            >
              <i className="fas fa-rotate-left"></i> Start new purchase
            </button>
          </div>
        </div>
      )}

      {!purchase && (
        <>
          <div className="cr-section">
            <h3 className="cr-section-title">
              <i className="fas fa-box"></i> Choose a Preset Package
            </h3>
            <div className="cr-packages-grid">
              {packages.map(p => (
                <div
                  key={p.id}
                  className={`cr-package ${selected?.id === p.id ? 'selected' : ''}`}
                  onClick={() => handleSelectPackage(p)}
                >
                  <div className="cr-package-check">
                    <i className="fas fa-check"></i>
                  </div>
                  <div className="cr-package-credits">{p.credits}</div>
                  <div className="cr-package-label">Credits</div>
                  <div className="cr-package-price">${p.price}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="cr-section">
            <h3 className="cr-section-title">
              <i className="fas fa-sliders-h"></i> Or Enter a Custom Amount
            </h3>
            <p className="cr-custom-subtitle">
              Buy any number of credits between {customLimits.min.toLocaleString()} and {customLimits.max.toLocaleString()}.
            </p>

            <div className="cr-custom-input-row">
              <div className="cr-custom-input-wrap">
                <i className="fas fa-coins cr-custom-input-icon"></i>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  placeholder={`Enter credits (min ${customLimits.min})`}
                  value={customCredits}
                  onChange={handleCustomChange}
                  className="cr-custom-input"
                />
                {customCredits && (
                  <button
                    type="button"
                    className="cr-custom-clear"
                    onClick={() => setCustomCredits('')}
                    title="Clear"
                  >
                    <i className="fas fa-times"></i>
                  </button>
                )}
              </div>
              <div className="cr-custom-quickadds">
                {[50, 100, 500].map(amt => (
                  <button
                    key={amt}
                    type="button"
                    className="cr-quickadd-btn"
                    onClick={() => handleQuickAdd(amt)}
                  >
                    +{amt}
                  </button>
                ))}
              </div>
            </div>

            {customCredits !== '' && !isCustomValid && (
              <div className="cr-custom-hint error">
                <i className="fas fa-exclamation-circle"></i>{' '}
                {customNumber < customLimits.min
                  ? `Minimum is ${customLimits.min} credits.`
                  : customNumber > customLimits.max
                  ? `Maximum is ${customLimits.max.toLocaleString()} credits.`
                  : `Must be a multiple of ${customLimits.step}.`}
              </div>
            )}
            {customCredits !== '' && isCustomValid && (
              <div className="cr-custom-hint ok">
                <i className="fas fa-check-circle"></i>{' '}
                You'll receive <strong>{customNumber.toLocaleString()}</strong> credits for{' '}
                <strong>${customNumber.toLocaleString()}</strong>.
              </div>
            )}
          </div>

          {canContinue && (
            <div className="cr-section">
              <h3 className="cr-section-title">
                <i className="fas fa-receipt"></i> Order Summary
              </h3>

              <div className="cr-summary">
                <div className="cr-summary-row">
                  <span>{selected ? 'Selected Package' : 'Custom Amount'}</span>
                  <span>{effectiveCredits.toLocaleString()} Credits</span>
                </div>
                <div className="cr-summary-row">
                  <span>Price</span>
                  <span>${effectiveAmount.toLocaleString()} USD</span>
                </div>
                <div className="cr-summary-row">
                  <span>Payment Method</span>
                  <span>Wire Payment</span>
                </div>
                <div className="cr-summary-row total">
                  <span>You'll Receive</span>
                  <span>{effectiveCredits.toLocaleString()} Credits</span>
                </div>
              </div>

              <div className="cr-actions-row">
                <button
                  className="cr-btn cr-btn-secondary"
                  onClick={() => {
                    setSelected(null);
                    setCustomCredits('');
                  }}
                >
                  Clear
                </button>
                <button
                  className="cr-btn cr-btn-primary"
                  onClick={handleCreatePurchase}
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i> Creating...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-arrow-right"></i> Continue to Payment
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {purchase && bankDetails && (
        <div className="cr-section">
          <h3 className="cr-section-title">
            <i className="fas fa-university"></i> Wire Payment Instructions
          </h3>
          <p className="cr-wire-instructions">
            Transfer <strong>{purchase.currency} {purchase.amount.toLocaleString()}</strong> to the account below, then upload your payment proof.
          </p>

          <div className="cr-bank">
            <div className="cr-bank-row">
              <span>Purchase ID</span>
              <span>{purchase.purchaseId}</span>
            </div>
            <div className="cr-bank-row">
              <span>Beneficiary Name</span>
              <span>{bankDetails.accountName}</span>
            </div>
            <div className="cr-bank-row">
              <span>Bank Name</span>
              <span>{bankDetails.bankName}</span>
            </div>
            <div className="cr-bank-row">
              <span>Bank Address</span>
              <span>{bankDetails.bankAddress}</span>
            </div>
            {bankDetails.accountNumber && purchase.currency !== 'EUR' && (
              <div className="cr-bank-row">
                <span>Account Number</span>
                <span>{bankDetails.accountNumber}</span>
              </div>
            )}
            {bankDetails.sortCode && (
              <div className="cr-bank-row">
                <span>Sort Code</span>
                <span>{bankDetails.sortCode}</span>
              </div>
            )}
            {bankDetails.routingNumber && (
              <div className="cr-bank-row">
                <span>Routing Number</span>
                <span>{bankDetails.routingNumber}</span>
              </div>
            )}
            {bankDetails.iban && (
              <div className="cr-bank-row">
                <span>IBAN</span>
                <span>{bankDetails.iban}</span>
              </div>
            )}
            <div className="cr-bank-row">
              <span>SWIFT/BIC</span>
              <span>{bankDetails.swiftCode}</span>
            </div>
            <div className="cr-bank-row">
              <span>Amount</span>
              <span className="cr-strong-green">{purchase.currency} {purchase.amount.toLocaleString()}</span>
            </div>
            <div className="cr-bank-row">
              <span>Credits to Receive</span>
              <span className="cr-strong-green">{purchase.credits.toLocaleString()} Credits</span>
            </div>
          </div>

          <div className="cr-form-group">
            <label>Upload Payment Proof *</label>
            <input type="file" accept="image/*" onChange={handleImageUpload} />
            {proofPreview && (
              <img
                src={proofPreview}
                alt="Proof preview"
                className="cr-proof-image"
                style={{ marginTop: 12 }}
              />
            )}
          </div>

          <div className="cr-form-group">
            <label>Notes (Optional)</label>
            <textarea
              rows="3"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Transaction reference or additional notes..."
            />
          </div>

          <div className="cr-actions-row">
            <button className="cr-btn cr-btn-secondary" onClick={handleBackToCredits}>
              Cancel
            </button>
            <button
              className="cr-btn cr-btn-primary"
              onClick={handleSubmitProof}
              disabled={submitting || !proofImage}
            >
              {submitting ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i> Submitting...
                </>
              ) : (
                <>
                  <i className="fas fa-paper-plane"></i> Submit Proof
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BuyCredits;